// Copyright (c) 2018 Comsearch, Inc., All rights reserved

import { initialize } from 'redux-form'
import { push } from 'react-router-redux'

import {
  PathStatusIdEnum,
  ProjectTypeIdEnum,
} from 'config/enums'

import {
  makeInitialPassiveValues,
  prepPathDetailsForForm,
  updatePassiveRepeaterStore,
} from 'redux/modules/pathDetails'

// this thunk initializes the Path Details form after a successful submit.
// then a path was just created on POST, then we will update the URL
// to go to that path's proper details page.
// the reason we reinitialize the form every time, even when we're about to go to
// a new path details page, is that we want to reset the page's behavior and
// no longer prevent page transition to the new path's URL.

const updatePathDetails = (pathData, isNewPath) => async (dispatch, getState) => {
  const {
    pathDetails,
    projectSummary,
    user,
  } = getState()

  const { pathId } = pathData
  const projectId = pathDetails.get('projectId')
  const withinProjectSummary = pathDetails.get('withinProjectSummary')

  // if we're here, we just updated an existing path,
  // we'll reinitialize the form with formatted pathData
  // that way any calculated values are presented,
  // any generated ids will be assigned and
  // the form becomes pristine
  const pathIndex = (
    withinProjectSummary
      ? pathDetails.get('pathIndex')
      : 'n/a'
  )

  const projectTypeId = (
    withinProjectSummary
      ? projectSummary.getIn(['projectType', 'projectTypeId'])
      : null
  )

  const domainId = user.get('userDomain')
  const sourceForm = pathDetails.get('sourceForm')
  const unitType = pathDetails.get('unitType')
  const initialValues = prepPathDetailsForForm(pathData, unitType, domainId)
  initialValues.pathIndex = pathIndex

  // Assign this value to redux-form's initial values so that
  // it knows to not validate the admin fields for
  // In Progress Paths on Engineering Projects.
  // See this form's sync validation handler for how this works.
  initialValues.hideAdminFields = (
    projectTypeId === ProjectTypeIdEnum.ENGINEERING &&
    initialValues.pathStatusId === PathStatusIdEnum.IN_PROGRESS
  )

  // if there are no passives for this path, the passive repeater's store
  // will automatically empty old data. otherwise, it will populate with the
  // path's passive data
  dispatch(updatePassiveRepeaterStore(initialValues.passiveRepeaters))

  // pass initial data set for passive repeater
  // if there isn't a passive repeater in the path's data.
  if (!initialValues.passiveRepeaters.length) {
    initialValues.passiveRepeaters.push(makeInitialPassiveValues(unitType))
  }

  await dispatch(initialize(sourceForm, initialValues))

  // if this was a newly created path, go to that path's URL
  if (isNewPath) {
    // determine if we are within project management.
    // base the next path to push to history based on this.
    const pathRoot = (
      withinProjectSummary
        ? `/project-management/${projectId}/path-details`
        : '/database-management/path'
    )

    return dispatch(push(`${pathRoot}/${pathId}`))
  }
}

export default updatePathDetails
